// ----- Tracker row 14 — optimistic concurrency, client side -----
//
// The server refuses a save made from an out-of-date copy (409 ProfileChanged /
// ProfileRowChanged) and returns what is there now. These helpers are the ONE
// client reading of that contract, shared by the profile editor and the
// contact / signatory row editors. See src/lib/db/optimisticVersion.ts.

import { ApiError, parseApiErrorBody } from '@/api/v2Client';

/** A refused save: what is stored now, and which fields differ from what was loaded. */
export interface SaveConflict {
  readonly current: Record<string, unknown>;
  readonly changedFields: readonly string[];
}

/** The conflict a 409 carries, or null for any other error. */
export function readSaveConflict(err: unknown): SaveConflict | null {
  if (!(err instanceof ApiError) || err.status !== 409) return null;
  const body = parseApiErrorBody(err);
  if (body?.error !== 'ProfileChanged' && body?.error !== 'ProfileRowChanged') return null;
  const data = body.data as { current?: unknown; changedFields?: unknown } | undefined;
  if (!data || typeof data.current !== 'object' || data.current === null) return null;
  return {
    current: data.current as Record<string, unknown>,
    changedFields: Array.isArray(data.changedFields)
      ? data.changedFields.filter((f): f is string => typeof f === 'string')
      : [],
  };
}

/** A branding write's version move (previous → current). */
export interface VersionMove {
  readonly previous: string | null;
  readonly current: string | null;
}

/**
 * The version an open editor should hold after one of ITS OWN writes moved it.
 * Adopt the new version only when the write replaced the version the editor
 * holds; otherwise someone else wrote in between, and keeping the old version
 * makes the next save report that change instead of overwriting it.
 */
export function adoptVersion(held: string | null, move: VersionMove | null | undefined): string | null {
  if (!move || move.current === null) return held;
  return move.previous !== null && move.previous === held ? move.current : held;
}

/** Comparable display form: blank, null and undefined are all "—". */
export function displayValue(value: unknown): string {
  if (value === undefined || value === null) return '—';
  if (typeof value === 'string') return value.trim() === '' ? '—' : value;
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'object') {
    const entries = Object.entries(value as Record<string, unknown>).filter(
      ([, v]) => v !== null && v !== undefined && v !== '',
    );
    return entries.length === 0 ? '—' : entries.map(([k, v]) => `${k}: ${String(v)}`).join(', ');
  }
  return String(value);
}

/** "AuthorizedSignatoryName" → "Authorized signatory name". */
export function fieldLabel(field: string): string {
  const words = field.replace(/([a-z0-9])([A-Z])/g, '$1 $2').replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2');
  return words.charAt(0).toUpperCase() + words.slice(1).toLowerCase();
}

/**
 * One row of "Review differences": the field, the value in the editor now
 * ("yours"), and the value stored now ("current").
 */
export interface FieldDifference {
  readonly field: string;
  readonly label: string;
  readonly yours: string;
  readonly current: string;
}

export function fieldDifferences(
  fields: readonly string[],
  yours: (field: string) => unknown,
  current: Record<string, unknown>,
): FieldDifference[] {
  return fields.map((field) => ({
    field,
    label: fieldLabel(field),
    yours: displayValue(yours(field)),
    current: displayValue(current[field]),
  }));
}
